import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trash2, ShoppingCart, Heart as HeartIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Wishlist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWishlist();
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wishlist`, {
        withCredentials: true,
      });
      setWishlist(data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/wishlist/${productId}`, {
        withCredentials: true,
      });
      toast.success('Item removed from wishlist');
      fetchWishlist();
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const addToCart = async (productId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/cart`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      toast.success('Added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C27054] mx-auto mb-4"></div>
          <p className="text-[#7A8A73]">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  const items = wishlist?.items || [];
  const isEmpty = items.length === 0;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-24 px-6" data-testid="wishlist-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73] mb-2">Favorites</p>
          <h1 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1]">
            Your Wishlist
          </h1>
        </div>

        {isEmpty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <HeartIcon size={64} className="mx-auto text-[#7A8A73] mb-4" />
            <p className="text-[#7A8A73] text-lg mb-6">Your wishlist is empty</p>
            <Link
              to="/products"
              data-testid="browse-products-button"
              className="inline-block bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold"
            >
              Browse Products
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {items.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group flex flex-col gap-4"
                data-testid={`wishlist-item-${product.id}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#E4E8DF]">
                  <Link to={`/product/${product.id}`}>
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
                    />
                  </Link>
                  <div className="absolute top-4 right-4 flex flex-col gap-2">
                    <button
                      onClick={() => removeItem(product.id)}
                      data-testid={`remove-item-${product.id}`}
                      className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors duration-300"
                    >
                      <Trash2 size={18} />
                    </button>
                    <button
                      onClick={() => addToCart(product.id)}
                      data-testid={`add-to-cart-${product.id}`}
                      className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-[#C27054] hover:text-white transition-colors duration-300"
                    >
                      <ShoppingCart size={18} />
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-start">
                  <div>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="text-lg font-serif text-[#2D3B26] hover:text-[#C27054] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-xs uppercase tracking-widest text-[#7A8A73] mt-1">
                      {product.metal_type}
                    </p>
                  </div>
                  <p className="text-sm font-sans font-medium text-[#475740]">₹{product.price}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;